import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDrag } from 'react-dnd';
import Icon from '../Icon';
import { StringNumber } from '../../types';
import DisplaySetMessageListTooltip from '../DisplaySetMessageListTooltip';

/**
 * Display a thumbnail for a display set.
 */
const Thumbnail = ({
  displaySetInstanceUID,
  className,
  imageSrc,
  imageAltText,
  description,
  seriesNumber,
  numInstances,
  loadingProgress,
  countIcon,
  messages,
  dragData = {},
  isActive,
  onClick,
  onDoubleClick,
}): React.ReactNode => {
  const [collectedProps, drag, dragPreview] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function (monitor) {
      return Object.keys(dragData).length !== 0;
    },
  });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const isMobileDevice = /(Mobi|Android|iPhone|iPad|iPod)/.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contenedorImagen = {
    width: '100px',
    ...(isMobile
      ? { opacity: '0.9', width: '70px', height: '70px', fontSize: '9px' }
      : { opacity: '1', width: '70px', height: '70px', fontSize: '10px' }),
  };

  const [lastTap, setLastTap] = useState(0);

  const handleTouchEnd = e => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 300 && tapLength > 0) {
      onDoubleClick(e);
    }
    setLastTap(currentTime);
  };

  return (
    <div
      className={classnames(
        className,
        'group mb-8 flex flex-1 cursor-pointer select-none flex-col px-0 outline-none'
      )}
      id={`thumbnail-${displaySetInstanceUID}`}
      data-cy={`study-browser-thumbnail`}
      data-series={seriesNumber}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onTouchEnd={handleTouchEnd}
      role="button"
      tabIndex="0"
    >
      <div ref={drag}>
        <div
          className={classnames(
            'min-h-10 relative flex flex-1 items-center justify-center overflow-hidden rounded-md text-base text-white',
            isActive ? 'border-primary-light border-2' : ''
          )}
          style={contenedorImagen}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={imageAltText}
              className="min-h-10 h-full w-full object-cover"
              crossOrigin="anonymous"
            />
          ) : (
            <div>{imageAltText}</div>
          )}
          <div
            className="pointer-events-none absolute top-0 left-0 h-16 w-full"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent)',
            }}
          ></div>
          <div
            className="pointer-events-none absolute bottom-0 left-0 h-16 w-full"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
            }}
          ></div>
          <div className="absolute top-0 right-1 flex items-center">
            <Icon
              name={countIcon || 'group-layers'}
              className="mr-1 w-3"
            />
            {numInstances}
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 transform text-center text-white">
            {description}
          </div>
        </div>
        <div className="flex flex-1 flex-row items-center text-base text-blue-300">
          <div className="mr-2 flex last:mr-0">
            {loadingProgress && loadingProgress < 1 && <>{Math.round(loadingProgress * 100)}%</>}
            {loadingProgress && loadingProgress === 1 && (
              <Icon
                name={'database'}
                className="w-3"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

Thumbnail.propTypes = {
  displaySetInstanceUID: PropTypes.string.isRequired,
  className: PropTypes.string,
  imageSrc: PropTypes.string,
  dragData: PropTypes.shape({
    type: PropTypes.string.isRequired,
  }),
  imageAltText: PropTypes.string,
  description: PropTypes.string.isRequired,
  seriesNumber: StringNumber.isRequired,
  numInstances: PropTypes.number.isRequired,
  loadingProgress: PropTypes.number,
  messages: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

export default Thumbnail;
